import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth, API } from "@/App";
import AppSidebar from "../components/AppSidebar";
import FormAnalyticsDashboard from "../components/FormAnalyticsDashboard";
import { 
  Users, 
  TrendingUp, 
  Target, 
  CheckCircle2,
  XCircle,
  Mail,
  Clock,
  ClipboardList,
  AlertCircle,
  Calendar,
  Loader2,
  BarChart3,
  FileText,
  Upload,
  ArrowRight,
  Bell,
  Sparkles,
  Timer,
  ArrowDown,
  Zap
} from "lucide-react";

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { user, logout, isLoading: authLoading } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await fetch(`${API}/analytics/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error("Failed to fetch analytics");
        
        const data = await response.json();
        setAnalytics(data);
      } catch (err) {
        console.error("Analytics fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return null;

  const lostReasonLabels = {
    "not_a_fit": "Not a Fit",
    "price": "Price",
    "no_response": "No Response",
    "chose_competitor": "Chose Competitor",
    "other": "Other"
  };

  // Calculate lead breakdown percentages
  const totalLeads = analytics?.overview?.total_leads || 0;
  const getPercentage = (count) => totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0;

  // Workflow performance data
  const wp = analytics?.workflow_performance || {};
  const hasWorkflowData = wp.funnel_conversions?.length > 0 || Object.keys(wp.automation_effectiveness || {}).length > 0;

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="analytics-page">
      <AppSidebar currentPath="/analytics" onLogout={handleLogout} />
      
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              Analytics
            </h1>
            <p className="text-muted-foreground mt-1">
              Track your business performance and outcomes
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <CardContent className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600">{error}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {/* Overview Section */}
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <MetricCard 
                    icon={Users} 
                    iconColor="text-blue-500" 
                    iconBg="bg-blue-50"
                    label="Total Leads" 
                    value={analytics?.overview?.total_leads || 0}
                  />
                  <MetricCard 
                    icon={TrendingUp} 
                    iconColor="text-emerald-500" 
                    iconBg="bg-emerald-50"
                    label="Conversion Rate" 
                    value={`${analytics?.overview?.conversion_rate || 0}%`}
                  />
                  <MetricCard 
                    icon={Target} 
                    iconColor="text-violet-500" 
                    iconBg="bg-violet-50"
                    label="Average Score" 
                    value={Math.ceil(analytics?.overview?.average_score || 0)}
                  />
                  <MetricCard 
                    icon={ClipboardList} 
                    iconColor="text-amber-500" 
                    iconBg="bg-amber-50"
                    label="Total Intakes" 
                    value={analytics?.overview?.total_intakes || 0}
                  />
                </div>
              </section>

              {/* Form Funnel — aggregate, drill-into per form */}
              <section data-testid="form-funnel-section">
                <FormAnalyticsDashboard />
              </section>

              {/* Lead Sources Section */}
              {analytics?.lead_sources && (
                <section>
                  <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Lead Sources
                  </h2>
                  <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {/* Source Cards */}
                        <div className="p-4 bg-blue-50 rounded-xl text-center">
                          <FileText className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                          <p className="text-2xl font-bold text-blue-700">{analytics.lead_sources.breakdown?.form || 0}</p>
                          <p className="text-xs text-blue-600">Form Submissions</p>
                          <p className="text-xs text-muted-foreground mt-1">{analytics.lead_sources.percentages?.form || 0}%</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-xl text-center">
                          <Users className="w-6 h-6 mx-auto mb-2 text-green-600" />
                          <p className="text-2xl font-bold text-green-700">{analytics.lead_sources.breakdown?.manual || 0}</p>
                          <p className="text-xs text-green-600">Manual Entry</p>
                          <p className="text-xs text-muted-foreground mt-1">{analytics.lead_sources.percentages?.manual || 0}%</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-xl text-center">
                          <Upload className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                          <p className="text-2xl font-bold text-purple-700">{analytics.lead_sources.breakdown?.import || 0}</p>
                          <p className="text-xs text-purple-600">CSV Import</p>
                          <p className="text-xs text-muted-foreground mt-1">{analytics.lead_sources.percentages?.import || 0}%</p>
                        </div>
                        {analytics.lead_sources.breakdown?.api > 0 && (
                          <div className="p-4 bg-amber-50 rounded-xl text-center">
                            <Zap className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                            <p className="text-2xl font-bold text-amber-700">{analytics.lead_sources.breakdown.api}</p>
                            <p className="text-xs text-amber-600">API</p>
                            <p className="text-xs text-muted-foreground mt-1">{analytics.lead_sources.percentages?.api || 0}%</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Manual Source Detail Breakdown */}
                      {analytics.lead_sources.manual_detail && Object.keys(analytics.lead_sources.manual_detail).length > 0 && (
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-medium mb-3 text-muted-foreground">Manual Entry Breakdown</h4>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(analytics.lead_sources.manual_detail).map(([detail, count]) => (
                              <Badge key={detail} variant="outline" className="bg-green-50 text-green-700">
                                {detail}: {count}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* ==================== WORKFLOW PERFORMANCE SECTION ==================== */}
              <section>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    Workflow Performance
                  </h2>
                  <p className="text-sm text-muted-foreground">Where are you losing clients and how automations are performing</p>
                </div>

                {!hasWorkflowData ? (
                  <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <CardContent className="p-12 text-center">
                      <BarChart3 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground font-medium">No data yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Submit a test lead to see performance insights</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {/* A. Conversion Funnel */}
                    <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                      <CardHeader className="pb-2 pt-5 px-6">
                        <CardTitle className="text-base font-medium flex items-center gap-2">
                          <Target className="w-4 h-4 text-primary" />
                          Conversion Funnel
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-6 pb-6">
                        <div className="space-y-3">
                          {wp.funnel_conversions?.map((conv, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="text-xs">{conv.from_label}</Badge>
                                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                  <Badge variant="secondary" className="text-xs">{conv.to_label}</Badge>
                                </div>
                                <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                                  <span>{conv.from_count}</span>
                                  <ArrowDown className="w-3 h-3" />
                                  <span>{conv.to_count}</span>
                                </div>
                              </div>
                              <div className={`text-2xl font-bold ${
                                conv.conversion_rate >= 70 ? 'text-green-600' : 
                                conv.conversion_rate >= 40 ? 'text-yellow-600' : 
                                'text-red-600'
                              }`}>
                                {conv.conversion_rate}%
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* B. Automation Effectiveness */}
                    {Object.keys(wp.automation_effectiveness || {}).length > 0 && (
                      <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <CardHeader className="pb-2 pt-5 px-6">
                          <CardTitle className="text-base font-medium flex items-center gap-2">
                            <Mail className="w-4 h-4 text-primary" />
                            Automation Effectiveness
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-6 pb-6">
                          <div className="grid grid-cols-2 gap-4">
                            {Object.entries(wp.automation_effectiveness).map(([status, data]) => (
                              <div key={status} className="p-4 bg-muted/30 rounded-xl">
                                <h4 className="font-medium capitalize mb-3 text-sm">{status.replace(/_/g, " ")}</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Emails sent</span>
                                    <Badge variant="outline" className="bg-green-50">{data.emails_sent}</Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Skipped</span>
                                    <Badge variant="outline" className="bg-gray-50">{data.emails_skipped}</Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Reminders</span>
                                    <Badge variant="outline" className="bg-yellow-50">{data.reminders_sent}</Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">AI follow-ups</span>
                                    <Badge variant="outline" className="bg-purple-50">{data.ai_followups}</Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Escalations</span>
                                    <Badge variant="outline" className="bg-red-50">{data.escalations}</Badge>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <div className="grid grid-cols-2 gap-6">
                      {/* C. Escalation Insights */}
                      <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <CardHeader className="pb-2 pt-5 px-6">
                          <CardTitle className="text-base font-medium flex items-center gap-2">
                            <Bell className="w-4 h-4 text-red-500" />
                            Escalation Insights
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-6 pb-6">
                          <div className="space-y-4">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Total escalations</span>
                              <span className="text-2xl font-bold">{wp.escalation_insights?.total_escalations || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Resolved after</span>
                              <span className="text-lg font-semibold text-green-600">{wp.escalation_insights?.resolution_rate || 0}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Avg hours stuck</span>
                              <span className="text-lg font-semibold">{wp.escalation_insights?.avg_hours_stuck || 0}h</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* E. Time to Conversion */}
                      <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <CardHeader className="pb-2 pt-5 px-6">
                          <CardTitle className="text-base font-medium flex items-center gap-2">
                            <Timer className="w-4 h-4 text-primary" />
                            Time to Conversion
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-6 pb-6">
                          <div className="space-y-3">
                            {wp.time_metrics?.map((metric, idx) => (
                              <div key={idx} className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground capitalize">
                                  {metric.from_stage.replace(/_/g, " ")} → {metric.to_stage.replace(/_/g, " ")}
                                </span>
                                <span className="font-medium">
                                  {metric.avg_days > 0 ? `${metric.avg_days}d` : '—'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* D. Reminder Effectiveness */}
                    {Object.keys(wp.reminder_effectiveness || {}).length > 0 && (
                      <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <CardHeader className="pb-2 pt-5 px-6">
                          <CardTitle className="text-base font-medium flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-yellow-500" />
                            Reminder Effectiveness
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-6 pb-6">
                          <div className="grid grid-cols-3 gap-4">
                            {Object.entries(wp.reminder_effectiveness).map(([status, data]) => (
                              <div key={status} className="p-4 bg-muted/30 rounded-xl text-center">
                                <h4 className="font-medium capitalize text-sm mb-2">{status.replace(/_/g, " ")}</h4>
                                <div className="text-3xl font-bold text-primary">{data.completion_rate}%</div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {data.completed_after_reminders} of {data.total_received_reminders} completed
                                </p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </section>

              {/* Lead Breakdown Section */}
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Lead Breakdown</h2>
                <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-5 gap-4">
                      <StatusCard 
                        icon={Clock} 
                        color="bg-blue-50 text-blue-600" 
                        label="New" 
                        value={analytics?.lead_breakdown?.new || 0}
                        percentage={getPercentage(analytics?.lead_breakdown?.new || 0)}
                      />
                      <StatusCard 
                        icon={Mail} 
                        color="bg-amber-50 text-amber-600" 
                        label="Contacted" 
                        value={analytics?.lead_breakdown?.contacted || 0}
                        percentage={getPercentage(analytics?.lead_breakdown?.contacted || 0)}
                      />
                      <StatusCard 
                        icon={Target} 
                        color="bg-violet-50 text-violet-600" 
                        label="Qualified" 
                        value={analytics?.lead_breakdown?.qualified || 0}
                        percentage={getPercentage(analytics?.lead_breakdown?.qualified || 0)}
                      />
                      <StatusCard 
                        icon={CheckCircle2} 
                        color="bg-emerald-50 text-emerald-600" 
                        label="Won" 
                        value={analytics?.lead_breakdown?.won || 0}
                        percentage={getPercentage(analytics?.lead_breakdown?.won || 0)}
                      />
                      <StatusCard 
                        icon={XCircle} 
                        color="bg-gray-50 text-gray-500" 
                        label="Lost" 
                        value={analytics?.lead_breakdown?.lost || 0}
                        percentage={getPercentage(analytics?.lead_breakdown?.lost || 0)}
                      />
                    </div>
                  </CardContent>
                </Card>
              </section>

              {/* Intake Funnel Section (renamed from Intake Metrics) */}
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Intake Funnel</h2>
                <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <FunnelStep 
                        label="Requested" 
                        value={analytics?.intake_breakdown?.waiting_for_intake || 0}
                        color="bg-gray-100 text-gray-600"
                      />
                      <ArrowRight className="w-5 h-5 text-gray-300 shrink-0" />
                      <FunnelStep 
                        label="Received" 
                        value={analytics?.intake_breakdown?.intake_received || 0}
                        color="bg-blue-50 text-blue-600"
                      />
                      <ArrowRight className="w-5 h-5 text-gray-300 shrink-0" />
                      <FunnelStep 
                        label="Completed" 
                        value={analytics?.intake_breakdown?.completed || 0}
                        color="bg-emerald-50 text-emerald-600"
                      />
                      <ArrowRight className="w-5 h-5 text-gray-300 shrink-0" />
                      <FunnelStep 
                        label="Rate" 
                        value={`${analytics?.intake_breakdown?.completion_rate || 0}%`}
                        color="bg-violet-50 text-violet-600"
                      />
                    </div>
                  </CardContent>
                </Card>
              </section>

              {/* Document Collection Performance Section */}
              {analytics?.document_collection?.requested > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Document Collection Performance
                  </h2>
                  
                  {/* Performance Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <MetricCard 
                      icon={FileText} 
                      iconColor="text-blue-500" 
                      iconBg="bg-blue-50"
                      label="Requested" 
                      value={analytics?.document_collection?.requested || 0}
                    />
                    <MetricCard 
                      icon={Upload} 
                      iconColor="text-emerald-500" 
                      iconBg="bg-emerald-50"
                      label="Submitted" 
                      value={analytics?.document_collection?.submitted || 0}
                    />
                    <MetricCard 
                      icon={TrendingUp} 
                      iconColor="text-violet-500" 
                      iconBg="bg-violet-50"
                      label="Completion Rate" 
                      value={`${analytics?.document_collection?.completion_rate || 0}%`}
                    />
                  </div>
                  
                  {/* Document Intake Funnel */}
                  <Card className="rounded-2xl border-0 bg-white mb-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <CardHeader className="pb-2 pt-5 px-6">
                      <CardTitle className="text-base font-medium">Document Intake Funnel</CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                      <div className="flex items-center justify-between">
                        <FunnelStep 
                          label="Awaiting" 
                          value={analytics?.document_collection?.requested - analytics?.document_collection?.submitted || 0}
                          color="bg-gray-100 text-gray-600"
                        />
                        <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                        <FunnelStep 
                          label="Clicked" 
                          value={analytics?.document_collection?.clicked || 0}
                          subtext={`${analytics?.document_collection?.click_rate || 0}%`}
                          color="bg-blue-50 text-blue-600"
                        />
                        <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                        <FunnelStep 
                          label="Submitted" 
                          value={analytics?.document_collection?.submitted || 0}
                          subtext={`${analytics?.document_collection?.click_to_submit_rate || 0}%`}
                          color="bg-emerald-50 text-emerald-600"
                        />
                        <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                        <FunnelStep 
                          label="Under Review" 
                          value={analytics?.document_collection?.under_review || 0}
                          color="bg-violet-50 text-violet-600"
                        />
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Time to Upload & Drop-off */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                      <CardHeader className="pb-2 pt-5 px-6">
                        <CardTitle className="text-base font-medium flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-500" />
                          Avg Time to Upload
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-6 pb-6">
                        <p className="text-3xl font-bold text-foreground">
                          {analytics?.document_collection?.avg_upload_hours || 0} <span className="text-lg font-normal text-muted-foreground">hours</span>
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Average time from request to submission
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                      <CardHeader className="pb-2 pt-5 px-6">
                        <CardTitle className="text-base font-medium flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          Drop-off Breakdown
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-6 pb-6">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Never clicked</span>
                            <span className="text-sm font-semibold">{analytics?.document_collection?.dropoff?.never_clicked || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Clicked, not submitted</span>
                            <span className="text-sm font-semibold">{analytics?.document_collection?.dropoff?.clicked_no_submit || 0}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-emerald-600">Submitted</span>
                            <span className="text-sm font-semibold text-emerald-600">{analytics?.document_collection?.dropoff?.submitted || 0}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Reminder Effectiveness (for documents) */}
                  {(analytics?.document_collection?.reminder_effectiveness?.after_r1 > 0 ||
                    analytics?.document_collection?.reminder_effectiveness?.after_r2 > 0 ||
                    analytics?.document_collection?.reminder_effectiveness?.after_r3 > 0) && (
                    <Card className="rounded-2xl border-0 bg-white mt-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                      <CardHeader className="pb-2 pt-5 px-6">
                        <CardTitle className="text-base font-medium flex items-center gap-2">
                          <Bell className="w-4 h-4 text-blue-500" />
                          Document Reminder Effectiveness
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-6 pb-6">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="p-3 rounded-xl bg-blue-50">
                            <p className="text-2xl font-bold text-blue-600">{analytics?.document_collection?.reminder_effectiveness?.after_r1 || 0}</p>
                            <p className="text-xs text-muted-foreground">After 1st reminder</p>
                          </div>
                          <div className="p-3 rounded-xl bg-amber-50">
                            <p className="text-2xl font-bold text-amber-600">{analytics?.document_collection?.reminder_effectiveness?.after_r2 || 0}</p>
                            <p className="text-xs text-muted-foreground">After 2nd reminder</p>
                          </div>
                          <div className="p-3 rounded-xl bg-red-50">
                            <p className="text-2xl font-bold text-red-600">{analytics?.document_collection?.reminder_effectiveness?.after_r3 || 0}</p>
                            <p className="text-xs text-muted-foreground">After 3rd reminder</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </section>
              )}

              {/* Lost Reasons Section */}
              {analytics?.lost_reasons && Object.keys(analytics.lost_reasons).length > 0 && (
                <section>
                  <h2 className="text-lg font-semibold text-foreground mb-4">Lost Reasons</h2>
                  <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-5 gap-4">
                        {Object.entries(analytics.lost_reasons).map(([reason, count]) => (
                          <div key={reason} className="text-center p-4 rounded-xl bg-gray-50">
                            <p className="text-2xl font-bold text-gray-700 leading-none mb-2">{count}</p>
                            <p className="text-xs text-muted-foreground">
                              {lostReasonLabels[reason] || reason}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* Activity Section */}
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Activity</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <CardHeader className="pb-2 pt-5 px-6">
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-blue-500" />
                        Leads
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                      <div className="flex gap-8">
                        <div>
                          <p className="text-2xl font-bold text-foreground">{analytics?.time_metrics?.leads_this_week || 0}</p>
                          <p className="text-sm text-muted-foreground/70">This Week</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-foreground">{analytics?.time_metrics?.leads_this_month || 0}</p>
                          <p className="text-sm text-muted-foreground/70">This Month</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <CardHeader className="pb-2 pt-5 px-6">
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-violet-500" />
                        Intakes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                      <div className="flex gap-8">
                        <div>
                          <p className="text-2xl font-bold text-foreground">{analytics?.time_metrics?.intakes_this_week || 0}</p>
                          <p className="text-sm text-muted-foreground/70">This Week</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-foreground">{analytics?.time_metrics?.intakes_this_month || 0}</p>
                          <p className="text-sm text-muted-foreground/70">This Month</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Metric Card Component
function MetricCard({ icon: Icon, iconColor, iconBg, label, value }) {
  return (
    <Card className="rounded-2xl border-0 bg-white transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground leading-none mb-1">{value}</p>
            <p className="text-sm text-muted-foreground/70">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Status Card Component - Updated with percentage
function StatusCard({ icon: Icon, color, label, value, percentage }) {
  return (
    <div className={`p-4 rounded-xl ${color} text-center`}>
      <Icon className="w-5 h-5 mx-auto mb-2 opacity-80" />
      <p className="text-2xl font-bold leading-none mb-1">{value}</p>
      <p className="text-xs opacity-80">{label}</p>
      {percentage !== undefined && (
        <p className="text-xs opacity-60 mt-1">({percentage}%)</p>
      )}
    </div>
  );
}

// Funnel Step Component
function FunnelStep({ label, value, subtext, color }) {
  return (
    <div className={`flex-1 p-4 rounded-xl ${color} text-center mx-1`}>
      <p className="text-2xl font-bold leading-none mb-1">{value}</p>
      <p className="text-xs font-medium">{label}</p>
      {subtext && <p className="text-xs opacity-70 mt-1">{subtext}</p>}
    </div>
  );
}
